import React from 'react';
import { useApolloClient } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/free-solid-svg-icons/faCoins';

import Button, {
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from '@buyside/components/presentation/Button/button';
import Link from '@buyside/components/presentation/Link/link';

import {
  accessIdeasUrl,
  buysideSignUpUrl,
  ideaUrl,
  newResearchUrl,
  qualityGuidelinesUrl
} from '@buyside/util/urlHelper';

import { IdeaCurrentUserQuery, usePurchaseIdeaMutation } from '@interfaces/graphql-typings';

const IdeaCurrentUser = require('buyside/graphql/queries/ideaShowPage/IdeaCurrentUser');

import styles from './styles.scss';

const AccessMoreContent: React.FC<{
  ideaId: string;
  isPublic: boolean;
}> = ({ ideaId, isPublic }) => {
  const client = useApolloClient();

  const cachedCurrentUser = client.readQuery<IdeaCurrentUserQuery>({
    query: IdeaCurrentUser,
    variables: { ideaId }
  })?.currentUser;

  const [spendIdeaToken, { loading, error }] = usePurchaseIdeaMutation();

  const { canWriteIdeas, isRetail } = cachedCurrentUser || {};
  const availableTokenCount = cachedCurrentUser?.availableTokenCount || 0;

  return (
    <div className={styles.accessMoreContent}>
      <h4>To Access More Content</h4>
      <div className={styles.wrapper}>
        {canWriteIdeas && (
          <div className={styles.optionContainer}>
            <div className={styles.optionName}>Contribute</div>
            <div className={styles.subtitle}>
              Share your ideas with the community.{' '}
              <Link className={styles.link} to={qualityGuidelinesUrl()} target="_blank">
                Check out our quality guidelines.
              </Link>
            </div>
            <Button
              className={styles.optionButton}
              text="Submit Research"
              type={BUTTON_TYPES.MAJOR}
              url={newResearchUrl()}
              width={BUTTON_WIDTHS.FULL}
            />
          </div>
        )}
        {isRetail && (
          <div className={styles.optionContainer}>
            <div className={styles.optionName}>Pay as you go</div>
            <div className={styles.subtitle}>Get on-demand access to ideas of your choice.</div>
            <Button
              className={styles.retailOptionButton}
              text="Buy Credits"
              type={BUTTON_TYPES.PRIMARY}
              url={accessIdeasUrl()}
              width={BUTTON_WIDTHS.FULL}
            />
            <div className={styles.availableToken}>
              <div className={styles.subtitle}>
                <FontAwesomeIcon icon={faCoins} className={styles.icon} />
                You have{' '}
                <strong>
                  {availableTokenCount} credit
                  {availableTokenCount > 1 || availableTokenCount === 0 ? 's' : ''}
                </strong>{' '}
                available
              </div>
              {availableTokenCount ? (
                <>
                  <Button
                    className={styles.retailOptionButton}
                    text="Unlock This Idea"
                    type={BUTTON_TYPES.MAJOR}
                    isLoading={loading}
                    width={BUTTON_WIDTHS.FULL}
                    onClick={async (): Promise<void> => {
                      try {
                        await spendIdeaToken({ variables: { ideaId } });
                        window.location.assign(ideaUrl(ideaId));
                      } catch (e) {
                        throw e;
                      }
                    }}
                  />
                  <div className={styles.msg}>The page will automatically reload</div>
                </>
              ) : null}
              {error && <div className={styles.error}>{error.graphQLErrors[0].message}</div>}
            </div>
          </div>
        )}
        <div className={styles.optionContainer}>
          {!isPublic ? (
            <>
              <div className={styles.optionName}>Purchase Membership</div>
              <div className={styles.subtitle}>
                Sign up online for comprehensive access to the research database and more.
              </div>
              <Button
                className={styles.optionButton}
                text="Sign Up"
                type={BUTTON_TYPES.PRIMARY}
                url={accessIdeasUrl()}
                width={BUTTON_WIDTHS.FULL}
              />
            </>
          ) : (
            <>
              <div className={styles.optionName}>
                Unlock trial access instantly by creating a free account.
              </div>
              <div className={styles.subtitle}>
                Use your trial to access research reports like this one and others across SumZero*.
                Once your trial expires, your free membership will continue to grant you a direct
                lens into the opportunities being discussed by our community of institutional
                buyside investors. You can upgrade your membership at any time to unlock complete
                access to any report.
              </div>
              <Button
                className={styles.optionButton}
                text="Create Account"
                type={BUTTON_TYPES.PRIMARY}
                url={buysideSignUpUrl()}
                width={BUTTON_WIDTHS.FULL}
              />
              <div className={styles.small}>*Limitations may apply.</div>
            </>
          )}
        </div>
      </div>
      <div className={styles.contactSales}>
        Contact{' '}
        <Link
          to={`mailto:sales@sumzero.com?subject=${encodeURIComponent(
            'Research Access'
          )}&body=${encodeURIComponent(
            'Hello,\n\nI am interested in learning more about research access.'
          )}`}
        >
          sales@sumzero.com
        </Link>{' '}
        to speak with a representative.
      </div>
    </div>
  );
};

export default AccessMoreContent;
